const API_BASE = import.meta.env.VITE_API_URL || '/api'

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  headers?: Record<string, string>
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  async request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {} } = options

    const config: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      credentials: 'include',
    }

    if (body) {
      config.body = JSON.stringify(body)
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, config)

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }))
      throw new Error(error.message || 'API request failed')
    }

    return response.json()
  }

  // Content endpoints
  async getContent() {
    return this.request<ContentItem[]>('/content')
  }

  async getContentById(id: string) {
    return this.request<ContentItem>(`/content/${id}`)
  }

  async createContent(data: CreateContentInput) {
    return this.request<ContentItem>('/content', { method: 'POST', body: data })
  }

  async updateContent(id: string, data: Partial<ContentItem>) {
    return this.request<ContentItem>(`/content/${id}`, { method: 'PUT', body: data })
  }

  async deleteContent(id: string) {
    return this.request<void>(`/content/${id}`, { method: 'DELETE' })
  }

  async publishContent(id: string, platforms: string[]) {
    return this.request<PublishResult>(`/content/${id}/publish`, { method: 'POST', body: { platforms } })
  }

  // AI Content endpoints
  async generateAIContent(data: AIContentRequest) {
    return this.request<AIGeneratedContent>('/ai-content/generate', { method: 'POST', body: data })
  }

  async getStagingQueue() {
    return this.request<AIGeneratedContent[]>('/ai-content/staging')
  }

  async approveContent(id: string) {
    return this.request<void>(`/ai-content/${id}/approve`, { method: 'POST' })
  }

  async rejectContent(id: string, reason?: string) {
    return this.request<void>(`/ai-content/${id}/reject`, { method: 'POST', body: { reason } })
  }

  // Platform endpoints
  async getPlatforms() {
    return this.request<PlatformAccount[]>('/platforms')
  }

  async connectPlatform(type: string) {
    return this.request<{ authUrl: string }>(`/platforms/connect/${type}`, { method: 'POST' })
  }

  async disconnectPlatform(id: string) {
    return this.request<void>(`/platforms/${id}`, { method: 'DELETE' })
  }

  // SEO endpoints
  async getSEOOverview() {
    return this.request<SEOOverview>('/seo/overview')
  }

  async syncSEOData() {
    return this.request<void>('/seo/sync', { method: 'POST' })
  }

  // Analytics endpoints
  async getAnalyticsOverview() {
    return this.request<AnalyticsOverview>('/analytics/overview')
  }

  // Intelligence endpoints
  async getIntelligencePulse() {
    return this.request<IntelligencePulse>('/intelligence/pulse')
  }

  async getOpportunities() {
    return this.request<Opportunity[]>('/intelligence/opportunities')
  }

  // Health check
  async getHealth() {
    return this.request<HealthStatus>('/../health')
  }

  async getFeatures() {
    return this.request<AppFeatures>('/..')
  }
}

export const api = new ApiClient(API_BASE)

// Types
export interface ContentItem {
  id: string
  title: string
  body: string
  platform: string
  status: 'draft' | 'scheduled' | 'published' | 'archived'
  scheduledAt?: string
  publishedAt?: string
  createdAt: string
  updatedAt: string
  metadata?: Record<string, unknown>
}

export interface CreateContentInput {
  title: string
  body: string
  platform: string
  status?: string
  scheduledAt?: string
  metadata?: Record<string, unknown>
}

export interface PublishResult {
  success: boolean
  postId?: string
  url?: string
  error?: string
}

export interface AIContentRequest {
  topic?: string
  keywords?: string[]
  platform: 'reddit' | 'twitter' | 'linkedin' | 'tiktok' | 'blog'
  contentType: 'post' | 'comment' | 'article' | 'video_script' | 'thread'
  tone?: 'professional' | 'casual' | 'humorous' | 'educational' | 'engaging'
  length?: 'short' | 'medium' | 'long'
  targetAudience?: string
  includeHashtags?: boolean
  seoKeywords?: string[]
  callToAction?: string
}

export interface AIGeneratedContent {
  id: string
  originalRequest: AIContentRequest
  aiProvider: 'anthropic' | 'openai' | 'gemini'
  generatedContent: {
    title?: string
    body: string
    hashtags?: string[]
    callToAction?: string
  }
  metadata: {
    estimatedViralScore: number
    seoScore: number
    engagementPrediction: number
    readabilityScore: number
    wordCount: number
  }
  status: 'generated' | 'staging' | 'under_review' | 'approved' | 'rejected' | 'published'
  createdAt: string
}

export interface PlatformAccount {
  id: string
  platform: string
  username?: string
  status: 'connected' | 'disconnected' | 'error'
  connectedAt?: string
}

export interface SEOOverview {
  properties: SEOProperty[]
  totalClicks: number
  totalImpressions: number
  averageCTR: number
  averagePosition: number
}

export interface SEOProperty {
  id: string
  domain: string
  type: 'google_search_console' | 'google_analytics' | 'bing'
  status: 'active' | 'error'
  lastSync?: string
}

export interface AnalyticsOverview {
  totalViews: number
  totalEngagements: number
  topContent: ContentItem[]
  platformBreakdown: Record<string, number>
}

export interface IntelligencePulse {
  trends: Trend[]
  opportunities: Opportunity[]
  recommendations: string[]
}

export interface Trend {
  id: string
  topic: string
  score: number
  source: string
  detectedAt: string
}

export interface Opportunity {
  id: string
  type: string
  title: string
  description: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  createdAt: string
}

export interface HealthStatus {
  status: string
  timestamp: number
  environment: string
}

export interface AppFeatures {
  version: string
  features: {
    contentManagement: boolean
    aiContentGeneration: boolean
    signalIntegration: boolean
    platforms: Record<string, boolean>
    ai: {
      anthropic: boolean
      openai: boolean
      gemini: boolean
      available: boolean
    }
    seo: {
      googleSearchConsole: boolean
      googleAnalytics: boolean
      bingWebmaster: boolean
    }
  }
}
